import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { Magnetic, WordReveal } from './Motion';

const TIME_OPTIONS = { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' };
const TIME_FORMATTER = new Intl.DateTimeFormat('en-IN', TIME_OPTIONS);
const MARQUEE_SEGMENTS = [
  { id: 'm1', a: 'AR Folio', b: 'Design × Intelligence' },
  { id: 'm2', a: 'AR Folio', b: 'Design × Intelligence' },
  { id: 'm3', a: 'AR Folio', b: 'Design × Intelligence' },
  { id: 'm4', a: 'AR Folio', b: 'Design × Intelligence' },
];
const MARQUEE_ANIM = { initial: { opacity: 0 }, whileInView: { opacity: 1 } };
const FOOTER_LINKS = [
  { id: 'colophon', label: 'Colophon', href: '#' },
  { id: 'style', label: 'Style Guide', href: '#' },
  { id: 'licenses', label: 'Image Licenses', href: '#' },
];

const ContactCTA = () => (
  <div className="md:col-span-2">
    <p className="text-muted text-[15px] leading-relaxed max-w-xl">
      The inbox is always open — product collaborations, AI experiences, design audits, or just a hello.
      I read everything.
    </p>
    <div className="mt-10 flex flex-wrap gap-4">
      <Magnetic>
        <a href="mailto:ar.folio26@gmail.com" className="btn-pill solid">
          ar.folio26@gmail.com <ArrowUpRight size={14}/>
        </a>
      </Magnetic>
      <Magnetic>
        <a href="https://www.linkedin.com/in/ar-singh" target="_blank" rel="noreferrer" className="btn-pill">LinkedIn</a>
      </Magnetic>
      <Magnetic>
        <a href="https://www.behance.net/amritraj5" target="_blank" rel="noreferrer" className="btn-pill">Behance</a>
      </Magnetic>
    </div>
  </div>
);

const LocationCard = ({ time }) => (
  <div className="flex flex-col gap-6 text-[13px]">
    <div>
      <p className="text-dim uppercase tracking-widest text-[11px]">Located</p>
      <p className="mt-2">Noida, India</p>
      <p className="text-muted">{time}</p>
    </div>
    <div>
      <p className="text-dim uppercase tracking-widest text-[11px]">Available</p>
      <p className="mt-2 flex items-center gap-2">
        <span className="inline-block w-2 h-2 rounded-full bg-accent animate-pulse"></span>
        Selectively, Q2 — Q3 2026
      </p>
    </div>
  </div>
);

const FooterMeta = () => (
  <div className="mt-24 pt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4" style={{ borderTop: '1px solid var(--line)' }}>
    <div className="flex items-center gap-3 text-[12px] text-dim">
      <span className="font-display">AR<span className="text-accent"> </span>Folio</span>
      <span>© 2026 — Product Design, UX Innovation & AI Experiences</span>
    </div>
    <div className="flex gap-6 text-[12px] text-dim">
      {FOOTER_LINKS.map((l) => (
        <a key={l.id} href={l.href} className="hover:text-accent">{l.label}</a>
      ))}
    </div>
  </div>
);

const FooterMarquee = () => (
  <motion.div className="mt-16 pt-8 overflow-hidden" {...MARQUEE_ANIM}>
    <div className="marquee-track h-display text-[14vw] md:text-[10vw] leading-none" style={{ color: 'rgba(168,167,181,0.18)' }}>
      {MARQUEE_SEGMENTS.map((s) => (
        <span key={s.id} className="flex items-center gap-12">
          <span>{s.a}</span>
          <span className="text-accent">•</span>
          <span className="font-serif-i">{s.b}</span>
          <span className="text-accent">•</span>
        </span>
      ))}
    </div>
  </motion.div>
);

export default function Footer() {
  const [time, setTime] = useState('');

  const updateTime = useCallback(() => {
    setTime(`${TIME_FORMATTER.format(new Date())} IST`);
  }, []);

  useEffect(() => {
    updateTime();
    const id = setInterval(updateTime, 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateTime]);

  return (
    <footer id="contact" className="relative px-6 md:px-12 lg:px-20 pt-32 pb-10 overflow-hidden">
      <div className="blob" style={{ width: 500, height: 500, background: 'var(--accent)', opacity: 0.08, left: -100, bottom: -200 }}></div>
      <div className="max-w-[1500px] mx-auto relative">
        <span className="section-eyebrow">Have an idea?</span>
        <h2 className="h-display mt-8 text-[14vw] md:text-[10vw] leading-[0.9]">
          <WordReveal text="Let's build"/>
          <br/>
          <span className="font-serif-i text-accent">something rare.</span>
        </h2>

        <div className="mt-16 grid md:grid-cols-3 gap-12">
          <ContactCTA />
          <LocationCard time={time} />
        </div>

        <FooterMeta />
      </div>

      <FooterMarquee />
    </footer>
  );
}
